<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.general.title')"
            :description="$t(tKey + '.general.description')"
          >
            <Multiselect
              v-model="rawFaq.section"
              :error="errors?.section"
              :options="faqSections"
              :label="$t('faqQuestion.section')"
              class="w-full"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
            />
            <TextInput
              v-model="rawFaq.orderNumber"
              type="number"
              name="orderNumber"
              class="mt-5 mb-10"
              :error="errors?.orderNumber"
              :label="$t('faqQuestion.orderNumber')"
            />
            <translation-accordion v-slot="{ translation }" v-model="rawFaq">
              <HtmlEditor
                v-model="(translation as FaqQuestionTranslation).question"
                name="question"
                :error="errors?.question"
                :label="$t('faqQuestion.question')"
              />
              <HtmlEditor
                v-model="(translation as FaqQuestionTranslation).answer"
                name="answer"
                class="mt-5"
                :error="errors?.answer"
                :label="$t('faqQuestion.answer')"
              />
            </translation-accordion>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  array, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import TextInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import { syncRef } from '@hooks/state';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import type {
  FaqQuestion, FaqQuestionTranslation, FaqSection,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  faq?: FaqQuestion;
}

const emit = defineEmits(['update:open', 'update:faq', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalFaq = useVModel(props, 'faq', emit);

const isNew = computed(() => !props.faq);
const tKey = computed(() => (isNew.value ? 'pages.faqList.modal.new' : 'pages.faqList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

interface RawTranslation {
  '@id'?: string;
  id?: string;
  question?: string;
  answer?: string;
  language: string;
}

const getMainTranslation = (translations: RawTranslation[]) => translations?.find((translation) => translation?.language === 'de');

// eslint-disable-next-line max-len
const getRawTranslations = (translations: RawTranslation[]): RawTranslation[] => translations.map((translation) => ({
  id: translation['@id'],
  question: translation.question,
  answer: translation.answer,
  language: translation.language,
}));

const initialTranslationData = (faq?: FaqQuestion): RawTranslation[] => (faq
  ? getRawTranslations(
    faq.translations,
  )
  : [
  ]);

const initialData = (faq?: FaqQuestion): { section: string | undefined, orderNumber: number | undefined } => (faq
  ? {
    section: faq.section?.['@id'],
    orderNumber: faq.orderNumber,
  }
  : {
    section: undefined,
    orderNumber: undefined,
  });

const rawFaq = syncRef(internalFaq, (faq) => ({ ...initialData(faq), translations: initialTranslationData(faq) }));

const {
  data: faq,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    section: string().nullable(),
    translations: array(
      object({
        question: string().nullable(),
        answer: string().nullable(),
      }),
    ),
    orderNumber: number().nullable().required(t('validation.notEmpty')),
  }),
  rawFaq,
);

function resetData() {
  rawFaq.value = { ...initialData() } as unknown as typeof faq['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalFaq.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.faq;
    const url = edit ? props.faq['@id'] : '/faq_questions';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        section: faq.value.section,
        translations: faq.value.translations.filter((trans) => trans),
        orderNumber: faq.value.orderNumber,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});

const faqSections = collOptionsProvider<FaqSection>('/faq_sections', {}, (value) => ({
  label:
    value.translations.find(({ language }) => language === 'de')?.title
    ?? value.translations[0]?.title
    ?? value.id,
  value: value['@id'],
}));
</script>

<style lang="scss" scoped></style>

